import React, { useMemo, useState } from 'react';
import { App, Button, Card, Col, Descriptions, Row, Space, Spin, Tag, Typography } from 'antd';
import { ArrowLeftOutlined, PrinterOutlined, SendOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkflowStrip, WorkspaceLayout } from '@/components/enterprise';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import { PrintableHrmsTransaction } from '@/components/hr/PrintableHrmsTransaction';
import { ENTITY_TYPE_HR_EMPLOYMENT_CONTRACT } from '@/constants/approval-entity-types';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { useEmployeeOptions } from './shared/useEmployeeOptions';
import { buildLinearWorkflow, hrmsStatusColor, showApprovalPanel } from './shared/hrmsStatus';
import { getOrFindFromList } from './shared/hrmsFetch';
import { openHrmsPrintOverlay } from './shared/openHrmsPrintOverlay';

const { Text } = Typography;

const ContractDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { empLabel } = useEmployeeOptions();
  const [loading, setLoading] = useState(false);

  const { data: row, isLoading } = useQuery({
    queryKey: hrmsKeys.contract(id || ''),
    queryFn: () =>
      getOrFindFromList(
        () => hrmsService.getContract(id!),
        () => hrmsService.listContracts(),
        id!
      ),
    enabled: Boolean(id),
  });

  const workflow = useMemo(
    () => buildLinearWorkflow(['Draft', 'Pending', 'Approved', 'Active'], row?.status || 'Draft'),
    [row?.status]
  );

  if (isLoading) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Employment contract" />}>
        <Spin />
      </WorkspaceLayout>
    );
  }
  if (!row) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Employment contract" />}>
        <Card>Not found</Card>
      </WorkspaceLayout>
    );
  }

  const submit = async () => {
    setLoading(true);
    try {
      await hrmsService.submitContract(id!);
      message.success('Submitted');
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.contract(id!) });
    } catch {
      message.error('Submit failed');
    } finally {
      setLoading(false);
    }
  };

  const printDoc = () => {
    openHrmsPrintOverlay((onClose) => (
      <PrintableHrmsTransaction
        onClose={onClose}
        data={{
          docType: 'HR_EMPLOYMENT_CONTRACT',
          title: 'Employment Contract',
          documentNumber: row.contract_number,
          employee_name: empLabel(row.employee_id),
          status: row.status,
          meta: [
            { label: 'Type', value: String(row.contract_type || '—') },
            { label: 'Version', value: String(row.version_number ?? '—') },
            { label: 'Location', value: String(row.work_location || '—') },
          ],
          lines: [
            { description: 'Basic salary', value: String(row.basic_salary ?? '—') },
            {
              description: 'Effective from',
              value: row.effective_from ? dayjs(row.effective_from).format('DD-MMM-YYYY') : '—',
            },
            {
              description: 'Effective to',
              value: row.effective_to ? dayjs(row.effective_to).format('DD-MMM-YYYY') : '—',
            },
            {
              description: 'Probation end',
              value: row.probation_end_date
                ? dayjs(row.probation_end_date).format('DD-MMM-YYYY')
                : '—',
            },
          ],
        }}
      />
    ));
  };

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Employment contract"
          documentNumber={row.contract_number  || '—'}
          statusTag={<Tag color={hrmsStatusColor(row.status)}>{row.status}</Tag>}
          subtitle={<Text type="secondary">{empLabel(row.employee_id)}</Text>}
          primaryActions={
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hr/contracts')}>
                Back
              </Button>
              <Button icon={<PrinterOutlined />} onClick={printDoc}>
                Print
              </Button>
              {(row.status === 'Draft' || row.status === 'Pending') && (
                <Button type="primary" icon={<SendOutlined />} loading={loading} onClick={submit}>
                  Submit
                </Button>
              )}
            </Space>
          }
        />
      }
      workflow={<WorkflowStrip stages={workflow.stages} current={workflow.current} compact />}
    >
      <Row gutter={16}>
        <Col xs={24} lg={16}>
          <Card title="Contract" style={{ marginBottom: 16 }}>
            <Descriptions column={{ xs: 1, sm: 2 }} size="small">
              <Descriptions.Item label="Employee">{empLabel(row.employee_id)}</Descriptions.Item>
              <Descriptions.Item label="Version">{row.version_number ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Type">{row.contract_type}</Descriptions.Item>
              <Descriptions.Item label="Basic">{row.basic_salary ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="From">
                {row.effective_from ? dayjs(row.effective_from).format('DD-MMM-YYYY') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="To">
                {row.effective_to ? dayjs(row.effective_to).format('DD-MMM-YYYY') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Probation end">
                {row.probation_end_date ? dayjs(row.probation_end_date).format('DD-MMM-YYYY') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Location">{row.work_location || '—'}</Descriptions.Item>
            </Descriptions>
          </Card>
          <EntityAttachmentsCard entityType="HR_EMPLOYMENT_CONTRACT" entityId={row.id} title="Contract attachments" />
        </Col>
        <Col xs={24} lg={8}>
          {showApprovalPanel(row) && (
            <ApprovalPanel
              entityType={ENTITY_TYPE_HR_EMPLOYMENT_CONTRACT}
              entityId={row.id}
              extraInvalidateQueryKeys={[hrmsKeys.contract(id!), hrmsKeys.contracts()]}
            />
          )}
        </Col>
      </Row>
    </WorkspaceLayout>
  );
};

export default ContractDetailPage;
